import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { History, Loader2, Shield, ShieldAlert, ShieldOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import insuranceService from '../../services/equipmentInsurance';
import type { EquipmentInsurance } from '../../types/equipmentInsurance';
import { COVER_TYPE_LABELS, TONE_CLASSES, insuranceState } from '../../utils/equipmentInsurance';
import { money } from '../../utils/nurseryMoney';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

interface EquipmentInsuranceCardProps {
  equipmentId: number;
  /** A road vehicle: one with no policy is flagged on the dashboard. */
  isRoadVehicle: boolean;
  /** vehicles creator or admin seat — adds, renews and marks as not needed. */
  canManage: boolean;
}

/**
 * The machine's insurance: the current policy it is on, whether that is due,
 * and the policies before it. The policy itself — every machine on it, the
 * schedule, renewing — lives on its own page; a farm pack covers many machines
 * and is renewed once, there.
 */
export default function EquipmentInsuranceCard({ equipmentId, isRoadVehicle, canManage }: EquipmentInsuranceCardProps) {
  const orgTz = useOrgTimezone();
  const navigate = useNavigate();
  const [data, setData] = useState<EquipmentInsurance | null>(null);
  const [failed, setFailed] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    insuranceService.forEquipment(equipmentId)
      .then(res => {
        if (res.success && res.data) { setData(res.data); setFailed(false); }
        else setFailed(true);
      })
      .catch(() => setFailed(true));
  }, [equipmentId]);

  useEffect(() => { load(); }, [load]);

  async function setNotRequired(value: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await insuranceService.setNotRequired(equipmentId, value);
      if (res.success && res.data) setData(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  const date = (d: string | null | undefined) => (d ? formatLocalDateInTz(d, orgTz) : '—');
  const current = data?.current ?? null;
  const state = current ? insuranceState(current) : null;
  const others = current?.machine_count ? current.machine_count - 1 : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4 text-neutral-500" />
          Insurance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {failed && !data ? (
          <p className="text-sm text-red-600">Could not read this machine’s insurance.</p>
        ) : !data ? (
          <p className="text-sm text-neutral-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : current && state ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-800 break-words">
                  {current.insurer} · {COVER_TYPE_LABELS[current.cover_type] ?? current.cover_type}
                </p>
                <p className="text-sm text-neutral-600 break-all">Policy {current.policy_number}</p>
                <p className="text-sm text-neutral-600">
                  {state.tone === 'expired' ? 'Expired' : 'Insured until'} {date(current.expiry_date)}
                </p>
              </div>
              <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0', TONE_CLASSES[state.tone])}>
                {state.label}
              </span>
            </div>
            {state.tone === 'expired' && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                This policy has lapsed. The machine is not covered until it is renewed.
              </p>
            )}
            {current.agreed_value != null && (
              <p className="text-sm text-neutral-600">Agreed value {money(current.agreed_value)}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to={`/equipment/insurance/${current.id}`} className="text-sm font-medium text-primary-600 hover:underline">
                Open policy
              </Link>
              {others > 0 && (
                <span className="text-xs text-neutral-500">
                  Also covers {others} other machine{others > 1 ? 's' : ''}
                </span>
              )}
            </div>
            {data.also_current.length > 0 && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This machine is also on{' '}
                {data.also_current.map((p, i) => (
                  <span key={p.id}>
                    {i > 0 && ', '}
                    <Link to={`/equipment/insurance/${p.id}`} className="underline break-all">{p.insurer} {p.policy_number}</Link>
                  </span>
                ))}
                . Check it is not insured twice.
              </p>
            )}
          </div>
        ) : data.insurance_not_required ? (
          <div className="space-y-2">
            <p className="text-sm text-neutral-600 flex items-start gap-2">
              <ShieldOff className="h-4 w-4 text-neutral-400 mt-0.5 flex-shrink-0" />
              Marked as not needing insurance, so it is not flagged as uninsured.
            </p>
            {canManage && (
              <Button size="sm" variant="outline" isLoading={busy} onClick={() => setNotRequired(false)}>
                It does need insurance
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-neutral-700 flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              No insurance recorded for this machine.
            </p>
            {canManage ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => navigate(`/equipment/insurance/new?equipment=${equipmentId}`)}>
                  Add a policy
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/equipment/insurance')}>
                  Add to an existing policy
                </Button>
                {isRoadVehicle && (
                  <Button size="sm" variant="ghost" isLoading={busy} onClick={() => setNotRequired(true)}>
                    Not insured, on purpose
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-xs text-neutral-500">Whoever manages the equipment register adds policies.</p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {data && data.past.length > 0 && (
          <div className="pt-1 border-t border-neutral-100">
            <button
              type="button"
              onClick={() => setShowPast(v => !v)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-800"
            >
              <History className="h-3.5 w-3.5" />
              {showPast ? 'Hide' : 'Show'} earlier policies ({data.past.length})
            </button>
            {showPast && (
              <ul className="mt-2 space-y-1.5">
                {data.past.map(p => (
                  <li key={p.id} className="text-sm flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                    <Link to={`/equipment/insurance/${p.id}`} className="text-neutral-700 hover:underline break-all min-w-0">
                      {p.insurer} {p.policy_number}
                    </Link>
                    <span className="text-xs text-neutral-500">
                      {date(p.start_date)} – {date(p.expiry_date)} · {p.status === 'renewed' ? 'Renewed' : 'Cancelled'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
